/**
 * Resolve in-app paths from notification action routes (native-safe).
 * Handles full URLs (listingUrl), snake_case ids, and broken /auctions// paths.
 */

import { parseExternalUrlToAppRoute } from "@/lib/native/parseAppUrl";

function pickString(
  data: Record<string, unknown> | undefined,
  ...keys: string[]
): string | undefined {
  if (!data) return undefined;
  for (const key of keys) {
    const value = data[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return undefined;
}

/** Build /auctions/:campaignId/:listingId|slug from notification payload fields. */
export function buildAuctionListingPath(
  data?: Record<string, unknown>,
): string | null {
  const campaignId = pickString(data, "campaignId", "campaign_id") || "all";
  const listingSegment =
    pickString(data, "listingSlug", "listing_slug", "slug") ||
    pickString(
      data,
      "listingId",
      "listing_id",
      "listingID",
      "vehicleId",
      "vehicle_id",
    );
  if (!listingSegment) return null;
  return `/listings/${encodeURIComponent(listingSegment)}`;
}

function collapseSlashes(path: string): string {
  return path.replace(/\/+/g, "/");
}

function isBrokenAuctionPath(path: string): boolean {
  if (!path.startsWith("/auctions")) return false;
  const parts = path.split("?")[0].split("/").filter(Boolean);
  if (parts.length < 3) return true;
  return parts.some((segment) => !segment);
}

/**
 * Returns an in-app path for react-router navigate(), or null if not navigable.
 * External HTTPS URLs (non-Bilenia hosts) return null — open via browser instead.
 */
export function resolveNotificationNavigationPath(
  route: string,
  data?: Record<string, unknown>,
): string | null {
  const trimmed = (route ?? "").trim();
  if (!trimmed) {
    return buildAuctionListingPath(data);
  }

  if (/^https?:\/\//i.test(trimmed)) {
    const inAppRoute = parseExternalUrlToAppRoute(trimmed);
    if (inAppRoute) {
      if (inAppRoute.startsWith("/auctions")) {
        const fixed = buildAuctionListingPath(data);
        if (fixed) return fixed;
        if (!isBrokenAuctionPath(inAppRoute)) return inAppRoute;
        return null;
      }
      return inAppRoute;
    }
    return buildAuctionListingPath(data);
  }

  let path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  path = collapseSlashes(path);

  if (path.startsWith("/auctions")) {
    const fixed = buildAuctionListingPath(data);
    if (fixed) return fixed;
    if (isBrokenAuctionPath(path)) return null;
  }

  return path;
}
